import { Link } from 'react-router-dom';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

// ---------------------------------------------------------------------------
// Column definitions — 13 columns (flat single-row header)
// ---------------------------------------------------------------------------
export const CAMPUS_COLUMN_DEFS = [
  { key: 'campus_name', label: 'Campus Name', sortable: true, filterable: true, required: true },
  { key: 'corporate_name', label: 'Corporation', sortable: true, filterable: true },
  { key: 'campus_street_address', label: 'Address', sortable: true, filterable: false },
  { key: 'campus_state', label: 'State', sortable: true, filterable: true },
  { key: 'campus_zip', label: 'Zip Code', sortable: true, filterable: true },
  { key: 'campus_email', label: 'Email', sortable: true, filterable: true },
  { key: 'campus_phone', label: 'Phone', sortable: true, filterable: true },
  { key: 'is_cloud', label: 'Platform', sortable: true, filterable: true },
  { key: 'is_resident_checkin', label: 'Check-in', sortable: true, filterable: true },
  { key: 'is_rounding', label: 'Rounds', sortable: true, filterable: true },
  { key: 'offsite_active', label: 'Offsite', sortable: true, filterable: true },
  { key: 'udp_broadcast', label: 'Light Ctrl', sortable: true, filterable: true },
  { key: 'service_plan_active', label: 'Service Plan', sortable: true, filterable: true },
];

/**
 * Platform label for a campus's `is_cloud` tinyint.
 *
 * 1 = 5.0 (cloud); 0 or NULL = 4.0 (on-prem). NULL is the older rows that predate
 * the column, and they are all 4.0, so absent and 0 mean the same thing here.
 *
 * `Number(value) === 1`, not `!!value`: MySQL tinyint reaches the portal as a number
 * on one path and a string on another, and `Boolean("0")` is **true** — a 4.0 campus
 * would have read as 5.0. Number("0") is 0, Number(null) is 0, and Number(undefined)
 * is NaN, so every non-1 value lands on 4.0.
 *
 * Exported and used by the cell, the filter option list, the page's filter matching
 * and the page's sort comparator, so those four cannot disagree about what a row is.
 */
export const PLATFORM_CLOUD = '5.0';
export const PLATFORM_ONPREM = '4.0';

export function platformLabel(isCloud) {
    return Number(isCloud) === 1 ? PLATFORM_CLOUD : PLATFORM_ONPREM;
}

/**
 * CampusesTable — data table for cross-campus list with
 * sorting, filtering, sticky header, row selection, and impersonate action.
 */
const CampusesTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onImpersonate,
}) => {
  const activeColumns = CAMPUS_COLUMN_DEFS.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      if (columnKey === 'is_cloud') {
        /* Always one of the two labels — a campus is never "(empty)" here, because
           a missing is_cloud is a 4.0 campus. */
        values.add(platformLabel(val));
      } else if (columnKey === 'service_plan_active') {
        values.add(val ? 'Plan Active' : 'No Plan');
      } else if (['is_resident_checkin', 'is_rounding', 'offsite_active', 'udp_broadcast'].includes(columnKey)) {
        values.add(val ? 'Yes' : 'No');
      } else {
        values.add(val != null && val !== '' ? String(val) : '(empty)');
      }
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const allSelected =
    data.length > 0 && data.every((r) => selectedIds.includes(r.campus_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Render a boolean checkmark or dash */
  const renderBoolIcon = (val) => {
    if (val) {
      return (
        <svg
          className="dp-icon-lg"
          style={{ color: 'var(--color-success, #10b981)' }}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      );
    }
    return <span className="dp-cell-text--muted">&mdash;</span>;
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'campus_name':
        return row.campus_name && row.campus_id ? (
          <Link
            to={'/profiles/campus/' + row.campus_id}
            className="dp-device-link"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            {row.campus_name}
          </Link>
        ) : (
          <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.campus_name || '\u2014'}
          </span>
        );

      case 'corporate_name':
        return row.corporate_name && row.corporate_id ? (
          <Link
            to={'/profiles/corporate/' + row.corporate_id}
            className="dp-device-link"
          >
            {row.corporate_name}
          </Link>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'campus_street_address':
        return (
          <div className="cp-stacked-cell">
            <span className="dp-cell-text">{row.campus_street_address || '\u2014'}</span>
            {row.campus_city ? (
              <span className="dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                {row.campus_city}
              </span>
            ) : null}
          </div>
        );

      case 'is_cloud':
        return (
          <span className="dp-cell-text">{platformLabel(row.is_cloud)}</span>
        );

      case 'is_resident_checkin':
      case 'is_rounding':
      case 'offsite_active':
      case 'udp_broadcast':
        return renderBoolIcon(row[columnKey]);

      case 'service_plan_active':
        return row.service_plan_active ? (
          <span className="dp-status dp-status--active">
            <span className="dp-status__dot" aria-hidden="true" />
            Plan Active
          </span>
        ) : (
          <span className="dp-status dp-status--inactive">
            <span className="dp-status__dot" aria-hidden="true" />
            No Plan
          </span>
        );

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="dp-th dp-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={onSelectAll}
                className="dp-checkbox"
                aria-label="Select all campuses"
              />
            </th>

            {/* Actions column */}
            <th scope="col" className="dp-th dp-th--actions"><span className="dp-sr-only">Impersonate</span></th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.campus_id);
            return (
              <tr
                key={row.campus_id}
                className={`dp-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="dp-td dp-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.campus_id)}
                    className="dp-checkbox"
                    aria-label={`Select ${row.campus_name}`}
                  />
                </td>

                {/* Row action: Impersonate */}
                <td className="dp-td dp-td--actions">
                  <CampusImpersonateButton
                    campus={row}
                    onImpersonate={onImpersonate}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="dp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}

              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * CampusImpersonateButton — the row's one action, Impersonate, as an eye icon button. It was a kebab menu
 * holding only this item, which took two clicks for the only thing in it.
 */
const CampusImpersonateButton = ({ campus, onImpersonate }) => (
  <button
    type="button"
    className="dp-row-actions__trigger"
    onClick={() => onImpersonate(campus)}
    title="Impersonate"
    aria-label={`Impersonate ${campus.campus_name}`}
  >
    <svg
      className="dp-icon-md"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  </button>
);

export default CampusesTable;
